import type { Object3D } from 'three';
import { Object3DState, TranslateOptions } from '../types/animations';
import { ComputedValues } from '../types/object';
import { Position } from '../types/position';
import { gsap } from 'gsap';
import { TimelineExt, TimelineWithPosition } from '../utils/combine';

type CoordKey = keyof Object3DState;

export function translateObject3D(o: Object3D, options?: TranslateOptions<Object3DState>): gsap.core.Timeline;
export function translateObject3D(o: Object3D, options: TranslateOptions<Object3DState>, position: gsap.Position): TimelineWithPosition;
export function translateObject3D(o: Object3D, options?: TranslateOptions<Object3DState>, position?: gsap.Position): TimelineExt {
    const { tl: existingTl, from, to, vars, timelineVars, reset = false } = options || { };
    let computed = options?.computed;

    const compute = (v: number | string): number => {
        const res = ComputedValues.parseComputed(computed, v);
        options.computed = computed = res.map;
        return res.value;
    };

    if (reset && from) {
        const set = (key: CoordKey) => {
            const v = Position.extract(from[key], compute);
            if (v) {
                o[key].x = v.x;
                o[key].y = v.y;
                o[key].z = v.z;
            }
        };

        set('position');
        set('scale');
        set('rotation');
    }

    const tl = existingTl || gsap.timeline(timelineVars);

    const add = (key: CoordKey) => {
        if (to && to[key]) {
            const t = Position.extract(to[key], compute);
            if (from && from[key]) {
                const f = Position.extract(from[key], compute);
                tl.fromTo(
                    o[key],
                    f,
                    { ...t, ...vars },
                    0,
                );
            } else {
                tl.to(
                    o[key],
                    { ...t, ...vars },
                    0,
                );
            }
        }
    };

    add('position');
    add('scale');
    add('rotation');

    if (position !== undefined) {
        return { tl, position };
    }

    return tl;
}
